import React, { useState, useEffect } from 'react';
import { Users, Search, Filter, MessageCircle, ShieldAlert, Sparkles, Rocket, Loader2, CheckCircle, Clock, Trash2, MapPin, Image as ImageIcon, HeartHandshake, ExternalLink, X, Phone, Eye, EyeOff } from 'lucide-react';
import Swal from 'sweetalert2';
import { API_ENDPOINTS, API_URL } from '../../config/api';

interface Member {
    id: number;
    name: string;
    gender: string;
    phone: string;
    generation: string;
    activity: string;
    domicile: string;
    is_interested_leader: boolean;
    leader_interest_area?: string;
    leader_reason?: string;
    concerns: string;
    goals: string;
    requested_topics?: string;
    speaker_recommendation?: string;
    donation_amount?: string;
    donation_proof?: string;
    follow_proof?: string;
    status: 'pending' | 'approved' | 'rejected';
    created_at: string;
    community_type: 'Sholehah' | 'Jofisah';
}

const MembersAdmin: React.FC = () => {
    const [members, setMembers] = useState<Member[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<'Sholehah' | 'Jofisah'>('Sholehah');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [previewModal, setPreviewModal] = useState<{ urls: string[]; title: string; activeIndex?: number } | null>(null);
    const [visiblePhoneIds, setVisiblePhoneIds] = useState<{ [id: number]: boolean }>({});
    const [isAllPhonesVisible, setIsAllPhonesVisible] = useState(false);

    // Helper untuk masking nomor HP (misal: 089663933263 -> 0896••••3263)
    const maskPhoneNumber = (phoneStr?: string): string => {
        if (!phoneStr) return '-';
        const clean = phoneStr.trim();
        const digits = clean.replace(/\D/g, '');
        if (digits.length >= 10) {
            const prefix = digits.slice(0, 4);
            const suffix = digits.slice(-4);
            return `${prefix}••••${suffix}`;
        }
        if (digits.length >= 6) {
            return `${digits.slice(0, 3)}••••${digits.slice(-2)}`;
        }
        return '••••••••';
    };

    // Format tampilan nomor HP (masked saat awal, atau rapi saat dibuka)
    const formatDisplayPhoneNumber = (phoneStr?: string, isVisible: boolean = false): string => {
        if (!phoneStr) return '-';
        if (!isVisible) {
            return maskPhoneNumber(phoneStr);
        }
        const digits = phoneStr.replace(/\D/g, '');
        if (digits.startsWith('0') && digits.length >= 10 && digits.length <= 13) {
            return `${digits.slice(0, 4)}-${digits.slice(4, 8)}-${digits.slice(8)}`;
        }
        return phoneStr;
    };

    const togglePhoneVisibility = (id: number) => {
        setVisiblePhoneIds(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    const toggleAllPhonesVisibility = () => {
        const nextState = !isAllPhonesVisible;
        setIsAllPhonesVisible(nextState);
        if (nextState) {
            const allVisible: { [id: number]: boolean } = {};
            members.forEach(m => { allVisible[m.id] = true; });
            setVisiblePhoneIds(allVisible);
        } else {
            setVisiblePhoneIds({});
        }
    };

    const isPhoneVisible = (id: number): boolean => {
        return Boolean(visiblePhoneIds[id]);
    };

    const getCleanWaNumber = (phoneStr?: string): string => {
        if (!phoneStr) return '';
        let clean = phoneStr.replace(/\D/g, '');
        if (clean.startsWith('0')) {
            clean = '62' + clean.slice(1);
        } else if (clean.startsWith('8')) {
            clean = '62' + clean;
        }
        return clean;
    };

    const parseProofUrls = (proofStr?: string | null): string[] => {
        if (!proofStr) return [];
        if (proofStr.startsWith('[') && proofStr.endsWith(']')) {
            try {
                const parsed = JSON.parse(proofStr);
                if (Array.isArray(parsed)) {
                    return parsed.map((item: string) => item.startsWith('http') ? item : `${API_URL}${item}`);
                }
            } catch {
                // fallback
            }
        }
        return proofStr.split(',').map(s => s.trim()).filter(Boolean).map(item => item.startsWith('http') ? item : `${API_URL}${item}`);
    };

    const fetchMembers = async (type: 'Sholehah' | 'Jofisah') => {
        setIsLoading(true);
        try {
            const endpoint = type === 'Sholehah' ? 'sholehah' : 'jofisah';
            const token = localStorage.getItem('khazanah_token');
            const res = await fetch(`${API_ENDPOINTS.members}/${endpoint}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await res.json();

            // Validasi: Pastikan 'data' adalah Array sebelum di-set
            if (res.ok && Array.isArray(data)) {
                setMembers(data);
            } else {
                console.error("Data bukan array atau API error:", data);
                setMembers([]); // Jadikan array kosong agar .filter tidak crash
            }

        } catch (error) {
            console.error("Gagal memuat data", error);
            Swal.fire('Error', 'Gagal memuat data member', 'error');
            setMembers([]); // Amankan dengan array kosong
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchMembers(activeTab);
    }, [activeTab]);

    const handleUpdateStatus = async (id: number, currentStatus: string, community: string) => {
        const nextStatus = currentStatus === 'pending' ? 'approved' : 'pending';
        const token = localStorage.getItem('khazanah_token');

        try {
            await fetch(`${API_ENDPOINTS.members}/${id}/status`, {
                method: 'PUT',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ community: community.toLowerCase(), status: nextStatus })
            });
            fetchMembers(activeTab); // Refresh data
        } catch (error) {
            console.error(error);
        }
    };

    const handleDelete = async (id: number, name: string, community: string) => {
        const result = await Swal.fire({
            title: `Hapus ${name}?`,
            text: "Data yang dihapus tidak dapat dikembalikan.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Ya, Hapus!'
        });

        if (result.isConfirmed) {
            try {
                const token = localStorage.getItem('khazanah_token');
                await fetch(`${API_ENDPOINTS.members}/${id}`, {
                    method: 'DELETE',
                    headers: { 
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ community: community.toLowerCase() })
                });
                Swal.fire('Terhapus!', 'Data berhasil dihapus.', 'success');
                fetchMembers(activeTab);
            } catch (error) {
                Swal.fire('Error', 'Gagal menghapus data', 'error');
            }
        }
    };

    // Filter Logic
    const filteredMembers = members.filter(m => {
        const matchSearch = m.name.toLowerCase().includes(searchTerm.toLowerCase()) || m.domicile.toLowerCase().includes(searchTerm.toLowerCase());
        const matchStatus = statusFilter === 'all' || m.status === statusFilter;
        return matchSearch && matchStatus;
    });

    const themeColor = activeTab === 'Sholehah' ? 'pink' : 'blue';

    return (
        <div className="p-4 lg:p-8 max-w-[100vw] mx-auto min-h-screen font-sans">

            {/* 1. HEADER & STATS */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                <div className="flex items-center gap-4">
                    <div className={`p-4 bg-${themeColor}-100 text-${themeColor}-600 rounded-2xl shadow-sm`}>
                        <Users size={32} />
                    </div>
                    <div>
                        <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Data Keanggotaan</h1>
                        <p className="text-gray-500 font-medium mt-1">Review, filter, dan kelola pendaftar baru Khazanah</p>
                    </div>
                </div>
            </div>

            {/* 2. TABS & FILTER TOOLBAR */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-2 mb-6 flex flex-col md:flex-row gap-4 items-center justify-between">

                {/* TABS */}
                <div className="flex bg-gray-50 p-1.5 rounded-xl w-full md:w-auto shrink-0 border border-gray-200/60">
                    <button
                        onClick={() => setActiveTab('Sholehah')}
                        className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-bold text-sm transition-all duration-300 ${activeTab === 'Sholehah' ? 'bg-white text-pink-600 shadow-sm border border-pink-100' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        <Sparkles size={16} /> Sholehah
                    </button>
                    <button
                        onClick={() => setActiveTab('Jofisah')}
                        className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-bold text-sm transition-all duration-300 ${activeTab === 'Jofisah' ? 'bg-white text-blue-600 shadow-sm border border-blue-100' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        <Rocket size={16} /> Jofisah
                    </button>
                </div>

                {/* SEARCH & STATUS FILTER */}
                <div className="flex w-full md:w-auto gap-3 flex-1 md:justify-end items-center flex-wrap">
                    <div className="relative flex-1 md:max-w-xs group">
                        <Search className={`absolute left-3 top-3 text-gray-400 group-focus-within:text-${themeColor}-500 transition-colors`} size={18} />
                        <input type="text" placeholder="Cari nama atau kota..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className={`w-full pl-10 pr-4 py-2.5 rounded-xl bg-gray-50 border-none ring-1 ring-gray-200 outline-none focus:ring-2 focus:ring-${themeColor}-400 focus:bg-white text-sm font-medium transition-all`} />
                    </div>
                    <div className="relative shrink-0 w-36">
                        <Filter className="absolute left-3 top-3 text-gray-400" size={18} />
                        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className={`w-full pl-10 pr-8 py-2.5 rounded-xl bg-gray-50 border-none ring-1 ring-gray-200 outline-none focus:ring-2 focus:ring-${themeColor}-400 text-sm font-bold text-gray-700 appearance-none cursor-pointer transition-all`}>
                            <option value="all">Semua Status</option>
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                        </select>
                    </div>
                    <button
                        type="button"
                        onClick={toggleAllPhonesVisibility}
                        className={`inline-flex items-center gap-2 px-3.5 py-2.5 rounded-xl border text-xs font-bold transition-all shrink-0 cursor-pointer ${
                            isAllPhonesVisible 
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100' 
                                : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100 hover:text-gray-900'
                        }`}
                        title={isAllPhonesVisible ? "Sembunyikan semua nomor (Masking)" : "Tampilkan semua nomor lengkap"}
                    >
                        {isAllPhonesVisible ? <EyeOff size={15} className="text-emerald-600" /> : <Eye size={15} className="text-gray-500" />}
                        <span className="hidden sm:inline">{isAllPhonesVisible ? 'Masking No. HP' : 'Tampilkan No. HP'}</span>
                    </button>
                </div>
            </div>

            {/* 3. LIST MEMBER CARDS (GRID) */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                {isLoading ? (
                    <div className="xl:col-span-2 py-20 flex flex-col items-center justify-center text-gray-400">
                        <Loader2 className={`animate-spin text-${themeColor}-500 mb-4`} size={40} />
                        <span className="font-medium">Memuat data pendaftar...</span>
                    </div>
                ) : filteredMembers.length === 0 ? (
                    <div className="xl:col-span-2 py-20 bg-white rounded-3xl border border-dashed border-gray-300 text-center flex flex-col items-center">
                        <Users className="text-gray-300 mb-3" size={48} />
                        <p className="text-gray-500 font-medium">Belum ada data pendaftar yang sesuai kriteria.</p>
                    </div>
                ) : (
                    filteredMembers.map((member) => (
                        <div key={member.id} className="bg-white rounded-3xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow overflow-hidden flex flex-col">

                            {/* Card Header */}
                            <div className={`p-5 flex justify-between items-start border-b border-gray-50 bg-gradient-to-r ${activeTab === 'Sholehah' ? 'from-pink-50/50 to-white' : 'from-blue-50/50 to-white'}`}>
                                <div className="flex gap-4 items-center">
                                    <div className={`w-14 h-14 rounded-2xl bg-${themeColor}-100 text-${themeColor}-600 flex items-center justify-center font-black text-xl shrink-0 border border-${themeColor}-200/50`}>
                                        {member.name.charAt(0).toUpperCase()}
                                    </div>
                                    <div>
                                        <div className="flex flex-wrap items-center gap-2 mb-1">
                                            <h3 className="text-lg font-black text-gray-900 leading-tight">{member.name}</h3>
                                            <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200/70 transition-all select-none">
                                                <a
                                                    href={`https://wa.me/${getCleanWaNumber(member.phone)}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="inline-flex items-center gap-1 hover:text-emerald-900 transition-colors"
                                                    title="Hubungi via WhatsApp"
                                                >
                                                    <Phone size={11} className="text-emerald-600 shrink-0" />
                                                    <span className="font-mono tracking-tight">
                                                        {formatDisplayPhoneNumber(member.phone, isPhoneVisible(member.id))}
                                                    </span>
                                                </a>
                                                <button
                                                    type="button"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        togglePhoneVisibility(member.id);
                                                    }}
                                                    className="p-0.5 ml-0.5 rounded text-emerald-600 hover:text-emerald-950 hover:bg-emerald-200/50 transition-colors cursor-pointer"
                                                    title={isPhoneVisible(member.id) ? "Sembunyikan nomor (Masking)" : "Tampilkan nomor lengkap"}
                                                >
                                                    {isPhoneVisible(member.id) ? <EyeOff size={12} /> : <Eye size={12} />}
                                                </button>
                                            </div>
                                        </div>
                                        <div className="flex flex-wrap items-center gap-2 text-xs font-bold text-gray-500">
                                            <span className="flex items-center gap-1"><MapPin size={12} /> {member.domicile}</span>
                                            <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
                                            <span>{member.activity}</span>
                                            <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
                                            <span>{member.generation}</span>
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={() => handleUpdateStatus(member.id, member.status, member.community_type)}
                                    className={`px-3 py-1.5 rounded-full text-xs font-black flex items-center gap-1.5 transition-colors ${member.status === 'approved' ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-amber-100 text-amber-700 hover:bg-amber-200'}`}
                                >
                                    {member.status === 'approved' ? <CheckCircle size={14} /> : <Clock size={14} />}
                                    {member.status.toUpperCase()}
                                </button>
                            </div>

                            {/* Card Body (Insights) */}
                            <div className="p-5 flex-1 space-y-4">
                                {Boolean(member.is_interested_leader) && (
                                    <div className={`bg-${themeColor}-50 border border-${themeColor}-100 p-3 rounded-xl flex items-start gap-3`}>
                                        <ShieldAlert className={`text-${themeColor}-600 shrink-0 mt-0.5`} size={18} />
                                        <div>
                                            <p className={`text-xs font-black uppercase text-${themeColor}-700 tracking-wider mb-1`}>Tertarik Menjadi Leader!</p>
                                            <p className={`text-sm font-semibold text-${themeColor}-900`}>{member.leader_interest_area}</p>
                                            {member.leader_reason && <p className="text-xs text-gray-600 mt-1 italic leading-relaxed line-clamp-2">"{member.leader_reason}"</p>}
                                        </div>
                                    </div>
                                )}

                                {(member.donation_amount || member.follow_proof || member.donation_proof) && (
                                    <div className="bg-emerald-50/70 border border-emerald-100 p-3 rounded-2xl flex flex-wrap items-center justify-between gap-2">
                                        <div className="flex items-center gap-2">
                                            <HeartHandshake className="text-emerald-600 shrink-0" size={18} />
                                            <div>
                                                <p className="text-[10px] font-black uppercase text-emerald-800 tracking-wider">Dukungan Khazanah</p>
                                                <p className="text-xs font-bold text-emerald-900">{member.donation_amount || 'Belum berdonasi'}</p>
                                            </div>
                                        </div>
                                        <div className="flex flex-wrap items-center gap-2">
                                            {member.donation_proof && (
                                                <button
                                                    type="button"
                                                    onClick={() => setPreviewModal({
                                                        urls: [member.donation_proof!.startsWith('http') ? member.donation_proof! : `${API_URL}${member.donation_proof}`],
                                                        title: `Bukti Infaq - ${member.name}`,
                                                        activeIndex: 0
                                                    })}
                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 text-xs font-bold shadow-xs transition cursor-pointer"
                                                >
                                                    <ImageIcon size={14} /> Bukti Infaq
                                                </button>
                                            )}
                                            {member.follow_proof && (() => {
                                                const followUrls = parseProofUrls(member.follow_proof);
                                                if (followUrls.length === 0) return null;
                                                return (
                                                    <button
                                                        type="button"
                                                        onClick={() => setPreviewModal({
                                                            urls: followUrls,
                                                            title: `Bukti Follow (${followUrls.length} Akun) - ${member.name}`,
                                                            activeIndex: 0
                                                        })}
                                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white border border-emerald-200 text-emerald-700 hover:bg-emerald-50 text-xs font-bold shadow-xs transition cursor-pointer"
                                                    >
                                                        <ImageIcon size={14} /> Bukti Follow {followUrls.length > 1 ? `(${followUrls.length})` : ''}
                                                    </button>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                )}

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="bg-gray-50 p-4 rounded-2xl border border-gray-100">
                                        <p className="text-[10px] font-black uppercase text-gray-400 tracking-wider mb-2">Keresahan / Masalah</p>
                                        <p className="text-sm text-gray-700 leading-relaxed line-clamp-3">{member.concerns}</p>
                                    </div>
                                    <div className="bg-gray-50 p-4 rounded-2xl border border-gray-100">
                                        <p className="text-[10px] font-black uppercase text-gray-400 tracking-wider mb-2">Harapan Gabung</p>
                                        <p className="text-sm text-gray-700 leading-relaxed line-clamp-3">{member.goals}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Card Footer (Actions) */}
                            <div className="p-4 border-t border-gray-50 bg-gray-50/50 flex justify-between items-center gap-3">
                                <div className="flex items-center gap-2 text-xs">
                                    <span className="text-gray-400 font-bold">
                                        Daftar: {new Date(member.created_at).toLocaleDateString('id-ID')}
                                    </span>
                                </div>
                                <div className="flex gap-2">
                                    <button onClick={() => handleDelete(member.id, member.name, member.community_type)} className="p-2.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors" title="Hapus Member">
                                        <Trash2 size={18} />
                                    </button>
                                    <a
                                        href={`https://wa.me/${getCleanWaNumber(member.phone)}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-5 py-2.5 rounded-xl text-sm font-bold shadow-md shadow-green-500/20 transition-all"
                                    >
                                        <MessageCircle size={18} /> Chat WA
                                    </a>
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Modal Preview Bukti Infaq / Bukti Follow */}
            {previewModal && (
                <div 
                    className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in duration-200"
                    onClick={() => setPreviewModal(null)}
                >
                    <div 
                        className="bg-white rounded-3xl max-w-2xl w-full overflow-hidden shadow-2xl border border-gray-100 flex flex-col max-h-[90vh]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-emerald-50/70">
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-xl bg-emerald-100 flex items-center justify-center text-emerald-700">
                                    <ImageIcon size={18} />
                                </div>
                                <h3 className="font-bold text-gray-800 text-sm">{previewModal.title}</h3>
                            </div>
                            <div className="flex items-center gap-2">
                                {previewModal.urls[previewModal.activeIndex || 0] && (
                                    <a
                                        href={previewModal.urls[previewModal.activeIndex || 0]}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 text-xs font-bold transition shadow-xs"
                                    >
                                        <ExternalLink size={14} /> Buka Tab Baru
                                    </a>
                                )}
                                <button
                                    type="button"
                                    onClick={() => setPreviewModal(null)}
                                    className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-xl transition cursor-pointer"
                                >
                                    <X size={20} />
                                </button>
                            </div>
                        </div>

                        {previewModal.urls.length > 1 && (
                            <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100 flex items-center gap-2 overflow-x-auto">
                                <span className="text-xs font-bold text-gray-500 shrink-0">Pilih Bukti ({previewModal.urls.length}):</span>
                                {previewModal.urls.map((_, i) => (
                                    <button
                                        key={i}
                                        type="button"
                                        onClick={() => setPreviewModal(prev => prev ? { ...prev, activeIndex: i } : null)}
                                        className={`px-3 py-1 rounded-xl text-xs font-bold transition cursor-pointer ${
                                            (previewModal.activeIndex || 0) === i
                                                ? 'bg-emerald-600 text-white shadow-xs'
                                                : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-100'
                                        }`}
                                    >
                                        Screenshot #{i + 1}
                                    </button>
                                ))}
                            </div>
                        )}

                        <div className="p-6 overflow-auto flex items-center justify-center bg-gray-900/5 min-h-[300px]">
                            {previewModal.urls[previewModal.activeIndex || 0] ? (
                                <img
                                    src={previewModal.urls[previewModal.activeIndex || 0]}
                                    alt={previewModal.title}
                                    className="max-h-[70vh] max-w-full object-contain rounded-2xl shadow-md"
                                />
                            ) : (
                                <p className="text-sm text-gray-400">Gambar tidak ditemukan</p>
                            )}
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default MembersAdmin;